import React, { useEffect, useMemo, useState } from "react";
import { Autocomplete, Box, Button, MenuItem, TextField } from "@mui/material";
import { IMasterValue } from "shared/utils/template/excelTemplate";
import SelectLabel from "components/atoms/SelectLabel";
import LabelRequired from "components/atoms/LabelRequired";
import {
  CUSTOM_MASTER_NAME_ATTRIBUTE,
  EXCEL_TEMPLATE_MASTER_NAME,
} from "@shared-constants";
import { TemplateExcelDataInfo } from "services/models";
import LoadingOverlayController from "@shared-components/loading/LoadingOverlayController";
import { getCustomMasterByBarcode } from "@api/masterType";
import { labeltextBreak } from "@utils/index";

interface IMasterInputField {
  setting: TemplateExcelDataInfo;
  value: IMasterValue;
  onChange: (value: IMasterValue) => void;
  reportItem: any;
  listMasterType: any;
  activityBaseId: string;
  setOrigin: (value: IMasterValue) => void;
  parent_id?: string;
}

interface ICustomItem {
  name: string;
  barcode: boolean;
  required: boolean;
  isName?: boolean;
}

const MasterInputField = ({
  setting,
  value,
  onChange,
  reportItem,
  listMasterType,
  activityBaseId,
  setOrigin,
  parent_id,
}: IMasterInputField) => {
  const [list, setList] = useState<Array<any>>([]);
  const [hasManualInput, setHasManualInput] = useState<boolean>(false);
  const [masterType, setMasterType] = useState<Array<ICustomItem>>([]);
  const [barcodeFlg, setBarcodeFlg] = useState<boolean>(false);
  const [search, setSearch] = useState("");
  const nameField = useMemo(() => {
    return setting.master && EXCEL_TEMPLATE_MASTER_NAME[setting.master]
      ? EXCEL_TEMPLATE_MASTER_NAME[setting.master]
      : CUSTOM_MASTER_NAME_ATTRIBUTE;
  }, [setting]);
  const otherTitle = (
    <>
      {"その他（"}
      {setting.name.includes("\n")
        ? labeltextBreak(setting.name)
        : setting.name}
      {"）"}
    </>
  );

  useEffect(() => {
    if (setting.master) setDataList(setting.master);
  }, [setting, reportItem, listMasterType, parent_id]);

  const setDataList = (master: string) => {
    if (reportItem && reportItem[master]) {
      let data_list = addWhiteSpace([...reportItem[master]]);
      if (parent_id && reportItem[master][parent_id]) {
        data_list = addWhiteSpace([...reportItem[master][parent_id]]);
        // 初期表示
        if (value?.id === null) {
          const index_default = list.findIndex(
            (item) => item?.is_default === true,
          );
          const custom_item: any[] =
            listMasterType[setting.master] &&
            listMasterType[setting.master]?.custom_item
              ? listMasterType[setting.master].custom_item
              : [];
          if (index_default !== -1) {
            const default_value: any = {
              id: list[index_default].SK,
              name: list[index_default][nameField],
              is_manual_input: list[index_default].is_manual_input,
            };
            // マスタ追加項目をマスタデータに追加
            if (custom_item) {
              custom_item.forEach((v, index) => {
                let key = v.barcode ? "barcode_item" : `item${index + 1}`;
                default_value[key] = list[index_default][key];
              });
            }
            onChange(default_value);
            setOrigin(default_value);
          }
        }
      }
      setList(data_list);
      setHasManualInput(
        data_list.some((item) => item?.is_manual_input === true),
      );
    }
    // 表示する項目を取得
    if (listMasterType && listMasterType[master]) {
      const custom_item: ICustomItem[] = listMasterType[master].custom_item;
      if (custom_item) {
        const barcode_flg = custom_item.some((v) => v.barcode);
        setBarcodeFlg(barcode_flg);
        setMasterType(custom_item);
      }
    }
  };

  const addWhiteSpace = (data: Array<any>) => {
    if (data.length == 0) {
      return data;
    }
    data.splice(0, 0, undefined);
    return data;
  };

  // マスタ検索
  const handleSearch = async (text?: string) => {
    try {
      LoadingOverlayController.show();
      var data = {
        SK: activityBaseId + "|" + setting.master,
        barcode_item: text ?? "",
      };
      const response = await getCustomMasterByBarcode(data);
      if (response.data && response.data.length > 0) {
        handleChangeValue(response.data[0]);
        setHasManualInput(response.data[0]?.is_manual_input);
        setList([response.data[0]]);
      } else {
        handleChangeValue();
        setHasManualInput(false);
        setList([]);
        setSearch("");
      }
    } finally {
      LoadingOverlayController.hide();
    }
  };

  // 入力
  const handleChangeValue = (selectedItem?: any) => {
    let newValue: any = {};
    if (selectedItem) {
      newValue = {
        id: selectedItem.SK,
        name: selectedItem.is_manual_input ? "" : selectedItem[nameField],
        is_manual_input: selectedItem.is_manual_input,
      };
    } else {
      newValue = {
        id: undefined,
        name: "",
        is_manual_input: false,
      };
    }
    // マスタ追加項目をマスタデータに追加
    masterType.forEach((custom_item, index) => {
      if (!custom_item.isName) {
        const key = custom_item.barcode ? "barcode_item" : `item${index + 1}`;
        newValue[key] = selectedItem ? selectedItem[key] : "";
      }
    });
    onChange(newValue);
  };

  const autocompleteValue = useMemo(() => {
    if (value && list) {
      const selected = list.find((item) => item && item.SK === value.id);
      return selected ? selected[nameField] : "";
    }
    return "";
  }, [value, list]);

  return (
    <>
      {!barcodeFlg ? (
        setting.autocomplete_flg ? (
          <Autocomplete
            freeSolo
            options={list.map((item) => (item ? item[nameField] : ""))}
            value={autocompleteValue}
            onInputChange={(e, newValue) => {
              let selected = list.find(
                (item) => item && item[nameField] === newValue,
              );
              if (!newValue || selected) handleChangeValue(selected);
            }}
            renderOption={(props: any, option: any) => {
              return (
                <li {...props}>
                  <em>{option || "未選択"}</em>
                </li>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label={
                  setting.required ? (
                    <LabelRequired title={labeltextBreak(setting.name)} />
                  ) : (
                    labeltextBreak(setting.name)
                  )
                }
              />
            )}
          />
        ) : (
          <SelectLabel
            label={
              setting.required ? (
                <LabelRequired title={labeltextBreak(setting.name)} />
              ) : (
                labeltextBreak(setting.name)
              )
            }
            value={value?.id ?? ""}
            onChange={(e) => {
              if (e.target.value != "") {
                const selectionItem = list.find(
                  (item) => item?.SK === e.target.value,
                );
                handleChangeValue(selectionItem);
              } else {
                handleChangeValue();
              }
            }}
          >
            {list.map((item, index) => {
              if (!item) {
                return (
                  <MenuItem value={""} key={index}>
                    <em>未選択</em>
                  </MenuItem>
                );
              } else {
                return (
                  <MenuItem value={item?.SK} key={index}>
                    {item[nameField]}
                  </MenuItem>
                );
              }
            })}
          </SelectLabel>
        )
      ) : (
        <Box sx={{ display: "flex" }}>
          <TextField
            label={
              setting.required ? (
                <LabelRequired title={labeltextBreak(setting.name)} />
              ) : (
                labeltextBreak(setting.name)
              )
            }
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
            }}
            sx={{ flex: 1 }}
          />
          <Box
            sx={{ display: "flex", alignSelf: "flex-end", ml: 1, mb: "10px" }}
          >
            <Button sx={{ minWidth: 60 }} onClick={() => handleSearch(search)}>
              検索
            </Button>
          </Box>
        </Box>
      )}
      {hasManualInput && (
        <TextField
          label={otherTitle}
          value={value?.is_manual_input ? value.name : ""}
          inputProps={{
            maxLength: 30,
          }}
          onChange={(e) => {
            onChange({ ...value, name: e.target.value });
          }}
          disabled={!value?.is_manual_input}
        />
      )}
      {barcodeFlg && !hasManualInput && (
        <TextField label={"名称"} value={value?.name} disabled />
      )}
      {masterType.map((custom_item, index) => {
        if (custom_item.barcode) return null;
        const selectionItem: any = value;
        const key = custom_item.isName ? "name" : `item${index + 1}`;
        return (
          <TextField
            label={custom_item.name}
            value={selectionItem ? selectionItem[key] : ""}
            disabled
            key={index}
          />
        );
      })}
    </>
  );
};

export default MasterInputField;
